import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { useLegalModal } from "@/components/LegalModal";
import { Button } from "@/components/ui/button";
import { Home, LayoutGrid, ShoppingCart, User, LogOut, Shield, PlusCircle, HelpCircle, Bell, Newspaper, Gavel, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Logo } from "./Logo";
import { CartDrawer } from "./CartDrawer";
import { NotificationsBell } from "./NotificationsBell";
import { LightVerifyModal } from "./LightVerifyModal";
import { SiteFooter } from "./SiteFooter";
import { AuctionPayReminder } from "./auction/AuctionPayReminder";
import { HeaderAvatar } from "./avatar/HeaderAvatar";
import { AvatarPromptGate } from "./avatar/AvatarPickerModal";

const SECTIONS = {
  home: (p) => p === "/",
  catalog: (p) => /^\/(catalog|card|carta)(\/|$)/.test(p),
  publish: (p) => p.startsWith("/publish") || p.startsWith("/publicar"),
  faq: (p) => p.startsWith("/faq"),
  auctions: (p) => p.startsWith("/subastas"),
};

const MobileIcon = ({ to, active, label, testid, icon: Icon }) => (
  <Link to={to} data-testid={testid} aria-label={label} aria-current={active ? "page" : undefined}
    className={`md:hidden w-10 h-10 grid place-items-center rounded-full transition-colors ${active ? "bg-[#EF4444]/15 text-[#EF4444]" : "text-neutral-300 active:text-[#EF4444]"}`}>
    <Icon size={20} />
  </Link>
);

const NavPill = ({ to, active, children, icon: Icon, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    aria-current={active ? "page" : undefined}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? "bg-[#EF4444] text-white"
        : "text-neutral-400 hover:text-white"
    }`}
  >
    {Icon && <Icon size={16} />}
    <span>{children}</span>
  </Link>
);

export function Layout({ children }) {
  const { user, loading, logout } = useAuth();
  const { cart, openDrawer } = useCart();
  const { open: openLegal } = useLegalModal();
  const loc = useLocation();
  const nav = useNavigate();
  const path = loc.pathname;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <header className="fixed top-0 inset-x-0 z-40 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#171717]" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 gap-2 sm:gap-4">
          <Link to="/" data-testid="header-logo" className="shrink-0">
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavPill to="/" active={SECTIONS.home(path)} icon={Home} testid="nav-home">Inicio</NavPill>
            <NavPill to="/catalog" active={SECTIONS.catalog(path)} icon={LayoutGrid} testid="nav-catalog">Catálogo</NavPill>
            {user && (
              <NavPill to="/publicar" active={SECTIONS.publish(path)} icon={PlusCircle} testid="nav-publish">Publicar</NavPill>
            )}
            <NavPill to="/faq" active={SECTIONS.faq(path)} icon={HelpCircle} testid="nav-faq">FAQ</NavPill>
            {/* Feb 2026 v31 — Subastas (reemplaza "Novedades" en la navegación
                pública). V1 stub: página "Próximamente" hasta que terminemos
                los slices del módulo (bidding, anti-sniping, cierre auto,
                repechaje, cobro por días). "Novedades" sigue vivo en /admin
                para changelog interno — no se perdió, solo salió del nav. */}
            <NavPill to="/subastas" active={SECTIONS.auctions(path)} icon={Gavel} testid="nav-auctions">Subastas</NavPill>
          </nav>
          <div className="flex items-center gap-0 sm:gap-2">
            {/* Mobile-only Catalog shortcut — replaces the notifications bell
                that used to sit here. Desktop already has the "Catálogo" nav
                pill so we hide this icon on md+. */}
            <MobileIcon to="/catalog" active={SECTIONS.catalog(path)} label="Ir al catálogo" testid="header-catalog-mobile" icon={LayoutGrid} />
            {user && <MobileIcon to="/publicar" active={SECTIONS.publish(path)} label="Publicar carta" testid="header-publish-mobile" icon={PlusCircle} />}
            <MobileIcon to="/faq" active={SECTIONS.faq(path)} label="Preguntas frecuentes" testid="header-faq-mobile" icon={HelpCircle} />
            <MobileIcon to="/subastas" active={SECTIONS.auctions(path)} label="Subastas" testid="header-auctions-mobile" icon={Gavel} />
            {/* Carrito, campana y avatar en un mismo grupo alineado */}
            <div className="flex items-center gap-2 md:gap-5" data-testid="header-icons-group">
            <button
              type="button"
              onClick={openDrawer}
              data-testid="header-cart"
              aria-label="Abrir carrito"
              className="relative w-10 h-10 grid place-items-center text-neutral-300 hover:text-white transition-colors"
            >
              <ShoppingCart size={20} strokeWidth={2} />
              {cart?.count > 0 && (
                <span className="absolute -top-[2px] -right-[4px] bg-[#EF4444] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-badge">
                  {cart.count}
                </span>
              )}
            </button>
            {loading && !user ? (
              <div className="w-24 h-9 rounded-md bg-[#171717] animate-pulse" data-testid="header-auth-pending" aria-label="Cargando sesión" />
            ) : user ? (
              <>
              <NotificationsBell />
              <div className="flex items-center ml-1 md:-ml-1" data-testid="header-avatar-block">
              <HeaderAvatar user={user} />
              <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="user-menu-trigger"
                    variant="ghost"
                    aria-label="Menú de cuenta"
                    aria-expanded={menuOpen}
                    className={`h-10 text-white gap-1 px-0.5 sm:px-2 flex items-center ${menuOpen ? "bg-[#EF4444]/15" : "hover:bg-[#171717]"}`}
                  >
                    <span className="hidden sm:inline text-sm">{user.name?.split(" ")[0]}</span>
                    <ChevronDown size={16} className={`transition-transform ${menuOpen ? "rotate-180 text-[#EF4444]" : "text-white/60"}`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#171717] border-[#262626] text-white w-56"
                  onInteractOutside={(e) => { if (e.target?.dataset?.testid === "user-menu-shield") e.preventDefault(); }}>
                  <DropdownMenuLabel className="text-neutral-400 text-xs">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  {(user.role === "admin" || user.role === "moderator") && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")} className="cursor-pointer hover:!bg-[#262626] focus:!bg-[#262626]">
                      <Shield size={14} className="mr-2" /> {user.role === "moderator" ? "Panel Moderador" : "Panel Admin"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="menu-buyer" onClick={() => nav("/buyer")} className="cursor-pointer hover:!bg-[#262626] focus:!bg-[#262626]">
                    <ShoppingCart size={14} className="mr-2" /> Mis Compras
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-seller" onClick={() => nav("/seller")} className="cursor-pointer hover:!bg-[#262626] focus:!bg-[#262626]">
                    <LayoutGrid size={14} className="mr-2" /> Mis Ventas
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-verify" onClick={() => nav("/verify")} className="cursor-pointer hover:!bg-[#262626] focus:!bg-[#262626]">
                    <User size={14} className="mr-2" /> Verificación ({user.verification_status})
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-2fa" onClick={() => nav("/setup-2fa")} className="cursor-pointer hover:!bg-[#262626] focus:!bg-[#262626]">
                      <Shield size={14} className="mr-2" /> 2FA {user.totp_enabled ? "✓" : "(configurar)"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer hover:!bg-[#262626] focus:!bg-[#262626] text-[#F87171]">
                    <LogOut size={14} className="mr-2" /> Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
              </>
            ) : (
              <Button
                data-testid="header-login"
                onClick={() => nav("/login")}
                className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-md px-5"
              >
                <User size={16} className="mr-2" /> Ingresar
              </Button>
            )}
            </div>
          </div>
        </div>
      </header>
      {menuOpen && (
        <div className="fixed inset-x-0 top-16 bottom-0 z-[45]" data-testid="user-menu-shield" aria-hidden="true"
             onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); }} />
      )}
      <div className="h-16 shrink-0" aria-hidden="true" />
      <main className="flex-1 min-h-[calc(100svh-64px)]">{children}</main>
      <AuctionPayReminder />
      <AvatarPromptGate path={path} />
      <CartDrawer />
      <LightVerifyModal />
      <SiteFooter />
    </div>
  );
}
